import { CheckCircleRounded, CloudUploadRounded, CloseRounded, UploadFile } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { Box } from '@mui/system'
import { AxiosError } from 'axios'
import { useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useMutation } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { SxProps, Theme } from '@mui/material/styles'
import { backendClient } from '../../interactors/clients/client'
import { useGlobalSnackbarStore } from '../../store/GlobalSnackBarStore'
import { useDialogOpened } from '../../utils/hooks/useDialogOpened'
import { setOpacity } from '../../utils/setOpacity'
import { InfoBox } from '../common/InfoBox'
import { blue, colors } from '../theme'

interface ServerError {
  reason: string
  // add other possible properties
}

interface AnimalSheetPopUpProps {
  buttonLabel?: string
  buttonSx?: SxProps<Theme>
}

export const AnimalSheetPopUp = ({
  buttonLabel = 'Importer mes animaux depuis un csv',
  buttonSx,
}: AnimalSheetPopUpProps) => {
  const { isDialogOpen, openDialog, closeDialog } = useDialogOpened()
  const globalSnackbar = useGlobalSnackbarStore()
  const navigate = useNavigate()
  const { getRootProps, getInputProps, isDragActive, acceptedFiles } = useDropzone({
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
    },
    maxFiles: 1,
  })

  const [selectedType, setSelectedType] = useState<'active' | 'old' | null>(null)
  const isDropZoneDisabled = !selectedType
  const [progress, setProgress] = useState(0)
  // This flag indicates that the submit button was clicked and we should start the progress bar.
  const [startProgress, setStartProgress] = useState(false)
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)
  const sendEmailToAdopter = false

  const handleToggleChange = (event: React.MouseEvent<HTMLElement>, newType: 'active' | 'old' | null) => {
    event.stopPropagation()
    setSelectedType(newType)
  }

  const resetAllAndClose = async () => {
    closeDialog()
    setStartProgress(false)
    setShowSuccessMessage(false)
    setProgress(0)
    setSelectedType(null)
    navigate(`/animaux?selectedTab=${selectedType === 'active' ? 'to-be-filled' : 'archived'}`)
  }

  const mutation = useMutation(
    async (file: File) => {
      const typeOfFile = selectedType === 'active' ? 'active' : 'old'
      const formData = new FormData()
      formData.append('file', file)
      return await backendClient.post(
        `animals/import?typeOfFile=${typeOfFile}&sendEmailToAdopter=${sendEmailToAdopter.toString()}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 300000, // 5 minutes timeout
        }
      )
    },
    {
      onError: (error: AxiosError) => {
        if (error.code === 'ECONNABORTED' || (error.message && error.message.toLowerCase().includes('timeout'))) {
          globalSnackbar.triggerInfoMessage(
            "L'import prend plus de temps que prévu et se poursuit en arrière-plan. Vos animaux pourront apparaître dans l'application dans quelques minutes. N'hésitez pas à rafraîchir la page."
          )
        } else {
          const errorMessage =
            error.response && error.response.data && (error.response.data as ServerError).reason
              ? (error.response.data as ServerError).reason
              : "L'import prend plus de temps que prévu et se poursuit en arrière-plan. Vos animaux pourront apparaître dans l'application dans quelques minutes. N'hésitez pas à rafraîchir la page."
          globalSnackbar.triggerErrorMessage(errorMessage)
        }
      },
    }
  )

  const onSubmit = async () => {
    if (acceptedFiles[0]) {
      // Start the progress immediately.
      setStartProgress(true)
      mutation.mutate(acceptedFiles[0])
    } else {
      globalSnackbar.triggerErrorMessage(`Vous devez ajouter un fichier !`)
    }
  }

  useEffect(() => {
    if (startProgress) {
      const timer = setInterval(() => {
        setProgress((oldProgress) => {
          // If progress reaches 99...
          if (oldProgress >= 99) {
            // If mutation is still loading, freeze progress
            if (mutation.isLoading) {
              return 99
            } else {
              // Mutation finished, complete the progress.
              clearInterval(timer)
              setShowSuccessMessage(true)
              return 100
            }
          }
          // Otherwise, increment progress normally.
          const diff = 100 / 15 // Increment such that it'll reach 100 in roughly 15 seconds.
          return Math.min(oldProgress + diff, 99)
        })
      }, 1000) // Update every second.

      return () => clearInterval(timer)
    }
  }, [startProgress, mutation.isLoading])

  return (
    <>
      <Button
        onClick={openDialog}
        sx={{
          textTransform: 'none',
          fontSize: '0.78rem',
          lineHeight: 1.2,
          padding: 0,
          minHeight: 'auto',
          color: colors.cello,
          textDecoration: 'underline',
          '&:hover': {
            backgroundColor: 'transparent',
            color: blue,
            textDecoration: 'underline',
          },
          ...buttonSx,
        }}
      >
        {buttonLabel}
      </Button>

      <Dialog
        open={isDialogOpen}
        onClose={closeDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography sx={{ color: colors.cello, fontSize: '1.25rem', fontWeight: 800 }}>
                Importer vos animaux
              </Typography>
              <Typography sx={{ color: '#7A8197', fontSize: '0.92rem' }}>
                Importez un export I-CAD pour remplir rapidement votre espace.
              </Typography>
            </Box>
            <IconButton onClick={closeDialog} sx={{ color: '#7A8197' }}>
              <CloseRounded />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
          {!startProgress && (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="stretch" gap={2}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  backgroundColor: '#F8F9FC',
                  border: '1px solid #E4E8F2',
                }}
              >
                <Typography sx={{ color: colors.cello, fontSize: '0.98rem', fontWeight: 700, mb: 1 }}>
                  1. Préparez votre fichier I-CAD
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={9} sx={{ display: 'flex', alignItems: 'center' }}>
                    <a href="https://www.i-cad.fr/" target="_blank" rel="noreferrer" style={{ color: colors.cello }}>
                      Pour gagner du temps, importez directement le fichier disponible dans votre espace I-CAD.
                    </a>
                  </Grid>
                  <Grid item xs={12} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
                    <a href="https://www.i-cad.fr/" target="_blank" rel="noreferrer">
                      <img
                        src="/images/i-cad.png"
                        alt="i-cad"
                        style={{ objectFit: 'cover', maxWidth: '100%', maxHeight: 40 }}
                      />
                    </a>
                  </Grid>
                </Grid>
              </Box>

              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  backgroundColor: '#F8F9FC',
                  border: '1px solid #E4E8F2',
                }}
              >
                <Typography sx={{ color: colors.cello, fontSize: '0.98rem', fontWeight: 700, mb: 1 }}>
                  2. Choisissez le type d&apos;import
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  value={selectedType}
                  onChange={handleToggleChange}
                  aria-label="text alignment"
                  fullWidth
                  sx={{
                    gap: 1,
                    '& .MuiToggleButtonGroup-grouped': {
                      borderRadius: '14px !important',
                      border: '1px solid #D7DDEA !important',
                      textTransform: 'none',
                      fontWeight: 700,
                      minHeight: 44,
                    },
                    '& .Mui-selected': {
                      backgroundColor: '#EEF2F8 !important',
                      color: colors.cello,
                      borderColor: '#BFC8DA !important',
                    },
                  }}
                >
                  <ToggleButton value="active" aria-label="Animaux suivis">
                    Animaux suivis
                  </ToggleButton>
                  <ToggleButton value="old" aria-label="Anciens animaux">
                    Anciens animaux
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  backgroundColor: '#F8F9FC',
                  border: '1px solid #E4E8F2',
                }}
              >
                <Typography sx={{ color: colors.cello, fontSize: '0.98rem', fontWeight: 700, mb: 1 }}>
                  3. Ajoutez votre fichier
                </Typography>
                <InfoBox
                  messageType="info"
                  content="Ne modifiez pas votre fichier : ne seront importé que les animaux qui ne sont pas déjà importé sur la base du numéro d’identification."
                />
                <Box
                  {...getRootProps({
                    sx: {
                      mt: 2,
                      p: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px dashed #BFC8DA',
                      borderRadius: 4,
                      backgroundColor: isDragActive
                        ? '#EEF2F8'
                        : acceptedFiles[0]
                        ? setOpacity(colors.geyser, 0.35)
                        : '#FFFFFF',
                      transition: 'all 0.2s ease',
                      pointerEvents: isDropZoneDisabled ? 'none' : 'auto',
                      opacity: isDropZoneDisabled ? 0.5 : 1,
                    },
                  })}
                >
                  <input {...getInputProps()} />
                  <CloudUploadRounded sx={{ color: blue, fontSize: 44, mb: 1 }} />
                  <Typography sx={{ color: colors.cello, fontSize: '1rem', fontWeight: 700, textAlign: 'center' }}>
                    Glissez-déposez votre fichier
                  </Typography>
                  <Typography sx={{ color: '#7A8197', fontSize: '0.88rem', textAlign: 'center', mt: 0.5 }}>
                    ou cliquez pour parcourir vos fichiers `.xlsx` ou `.csv`
                  </Typography>
                  <Box
                    mt={2}
                    sx={{
                      width: '100%',
                      maxWidth: 420,
                      px: 1.5,
                      py: 1.25,
                      borderRadius: 3,
                      backgroundColor: '#F8F9FC',
                      border: '1px solid #E4E8F2',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <UploadFile sx={{ color: '#7A8197' }} />
                    <Typography sx={{ color: acceptedFiles[0] ? colors.cello : '#7A8197', fontSize: '0.9rem' }} noWrap>
                      {acceptedFiles[0]?.name || 'Aucun fichier sélectionné'}
                    </Typography>
                  </Box>
                  <Button variant="contained" sx={{ mt: 2, minWidth: 160 }}>
                    Parcourir
                  </Button>
                </Box>
              </Box>
              <Typography sx={{ color: '#7A8197', fontSize: '0.88rem', textAlign: 'center' }}>
                Une fois l&apos;import terminé, vous pourrez enrichir les fiches animal directement dans Petso.
              </Typography>
            </Box>
          )}

          {startProgress && (
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', mt: 1, gap: 1.5 }}>
              <Typography sx={{ color: colors.cello, fontWeight: 700 }}>
                Merci de patienter pendant que nous traitons vos données...
              </Typography>
              <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 999 }} />
            </Box>
          )}

          {showSuccessMessage && (
            <Box sx={{ textAlign: 'center', pt: 1 }}>
              <CheckCircleRounded sx={{ color: '#2F7D32', fontSize: 36, mb: 1 }} />
              <Typography sx={{ color: '#2F7D32', fontWeight: 800 }}>Traitement terminé avec succès</Typography>
              {selectedType === 'old' && (
                <Typography sx={{ color: colors.cello, mt: 1.5 }}>
                  Nous avons ajouté tous les animaux du fichier &quot;Mes anciens animaux&quot;. Par défaut, chaque
                  animal a été enregistré comme adopté. <strong>Si ce n&apos;est pas le cas de certains,</strong>
                  pensez à corriger l&apos;information dans l&apos;onglet &quot;adoption&quot; de leur fiche.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, pt: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, width: '100%' }}>
            {showSuccessMessage ? (
              <Button
                data-testid="terminate"
                type="button"
                variant="contained"
                onClick={() => {
                  resetAllAndClose()
                }}
                sx={{ textTransform: 'none', borderRadius: '999px', px: 3, fontWeight: 700, boxShadow: 'none' }}
              >
                Terminer
              </Button>
            ) : (
              <>
                {!startProgress && (
                  <Button
                    data-testid="cancel"
                    type="button"
                    onClick={closeDialog}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '999px',
                      px: 2,
                      fontWeight: 700,
                    }}
                  >
                    Revenir
                  </Button>
                )}
                <LoadingButton
                  data-testid="submit"
                  variant="contained"
                  onClick={onSubmit}
                  disabled={!acceptedFiles[0] || isDropZoneDisabled}
                  loading={mutation.isLoading || startProgress}
                  sx={{ textTransform: 'none', borderRadius: '999px', px: 3, fontWeight: 700, boxShadow: 'none' }}
                >
                  Importer
                </LoadingButton>
              </>
            )}
          </Box>
        </DialogActions>
      </Dialog>
    </>
  )
}
